import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes, UrlTree } from '@angular/router';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { Observable, map, take } from 'rxjs';

/**
 * Emits the current Firebase user (or null) once the SDK has restored the
 * session, then completes. Replaces `@angular/fire/auth-guard`, whose `Auth`
 * provider is broken under Angular 21 / @angular/fire v1 RC (NG0200 circular
 * dependency). Uses the raw Firebase SDK, consistent with the rest of the app.
 */
function firstAuthState(): Observable<User | null> {
  return new Observable<User | null>((subscriber) => {
    const unsubscribe = onAuthStateChanged(
      getAuth(),
      (user) => subscriber.next(user),
      (error) => subscriber.error(error),
    );
    return { unsubscribe };
  });
}

/** Allow if authenticated; otherwise redirect to the sign-in page. */
const redirectUnauthorizedToLogin: CanActivateFn = (): Observable<
  boolean | UrlTree
> => {
  const router = inject(Router);
  return firstAuthState().pipe(
    take(1),
    map((user) => (user ? true : router.createUrlTree(['account/sign-in']))),
  );
};

/** Allow if NOT authenticated; otherwise redirect to the main page. */
const redirectLoggedInToMainPage: CanActivateFn = (): Observable<
  boolean | UrlTree
> => {
  const router = inject(Router);
  return firstAuthState().pipe(
    take(1),
    map((user) => (user ? router.createUrlTree(['/']) : true)),
  );
};

/**
 * Defines the routes for the application.
 */
export const routes: Routes = [
  {
    path: '',
    loadChildren: () => import('./pages/shell/shell.routes').then((m) => m.routes),
    canActivate: [redirectUnauthorizedToLogin],
  },
  {
    path: 'account',
    loadChildren: () => import('./pages/account/account.routes').then((m) => m.routes),
    canActivate: [redirectLoggedInToMainPage],
    data: {
      breadcrumb: 'Account',
    },
  },
  {
    path: 'payment/:clientSecret',
    loadChildren: () => import('./pages/payment/payment.routes').then((m) => m.routes),
    canActivate: [redirectUnauthorizedToLogin],
    data: {
      breadcrumb: 'Payment',
    },
  },
  {
    path: 'restricted',
    loadChildren: () => import('./pages/restricted-content/restricted-content.routes').then((m) => m.routes),
  },
  {
    path: 'not-found',
    loadChildren: () => import('./pages/not-found/not-found.routes').then((m) => m.routes),
  },
  {
    path: '**',
    loadChildren: () => import('./pages/not-found/not-found.routes').then((m) => m.routes),
  },
];
